import { useCallback, useEffect } from "react"
import {
  Alert,
  Linking,
  Platform,
  StatusBar,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native"

import { useFocusEffect } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { Button, Text } from "src/components/Ignite"
import BaseConfig from "src/config/config.base"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useBackendAPIVersion } from "src/queries/useApi"
import { useCleanupThenLogout } from "src/queries/useUser"
import { ThemedStyle } from "src/theme"
import { logSentryBreadcrumb } from "src/utils/crashReporting"

/**
 * Shows the user a message that their app is outdated and they need to update.
 * This is based on the backend API version, which needs to match in the app version.
 *
 * This page is shown after the user logs in (because it's based on which server they authenticate
 * with) and only offers a single option: log out. dev app users can switch to a different server
 * with the proper API version to avoid this screen.
 */
export default function OutdatedScreen() {
  const safeInsets = useSafeAreaInsets()
  const { themed } = useAppTheme()
  const { data: backendAPIVersion } = useBackendAPIVersion()
  const logoutMutation = useCleanupThenLogout()

  // Lock to portrait orientation when this screen gains focus
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {})
    }, []),
  )

  useEffect(() => {
    logSentryBreadcrumb(`outdated_screen_viewed:user_version_${backendAPIVersion}`)
  }, [backendAPIVersion])

  const handleUpdate = () => {
    if (Platform.OS === "android") {
      Linking.openURL("https://play.google.com/store/apps/details?id=com.sidekickwellness.chat")
    } else if (Platform.OS === "ios") {
      Linking.openURL("https://apps.apple.com/us/app/sidekick-platform/id6504539500")
    } else {
      Alert.alert("Unsupported Platform", "Please update the app to continue.")
    }
  }

  const handleLogout = () => {
    logoutMutation.mutate()
  }

  return (
    <View style={{ ...$flex, marginBottom: safeInsets.bottom }}>
      <StatusBar barStyle="light-content" />
      <View style={themed($container)}>
        <Text text="Update Required" preset="heading" style={themed($centeredText)} />
        {BaseConfig.appEnv === "development" ? (
          <>
            <Text
              text="We've upgraded the Sidekick Platform to support new features."
              style={themed($centeredText)}
            />
            <Text text="Update now to continue using the app." style={themed($centeredText)} />
          </>
        ) : (
          <>
            <Text text="Connection Error." style={themed($centeredText)} />
            <Text
              text=" We were able to connect to the Sidekick server but there was a version mismatch."
              style={themed($centeredText)}
            />
            <Text text="This should be resolved shortly." style={themed($centeredText)} />
          </>
        )}
        <Button
          text={"Update Now"}
          onPress={handleUpdate}
          preset="sidekick"
          style={{ width: "100%" }}
        />
      </View>

      {/* Subtle logout link */}
      <TouchableOpacity
        onPress={handleLogout}
        style={[themed($logoutLink), { marginBottom: safeInsets.bottom + 16, marginRight: 16 }]}
        disabled={logoutMutation.isPending}
      >
        <Text
          text={logoutMutation.isPending ? "Logging out..." : "Log out"}
          style={themed($logoutText)}
        />
      </TouchableOpacity>
    </View>
  )
}

const $flex: ViewStyle = { flex: 1 }

const $container: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  flex: 1,
  backgroundColor: colors.background,
  justifyContent: "center",
  paddingHorizontal: spacing.lg,
  gap: spacing.lg,
})

const $centeredText: ThemedStyle<TextStyle> = ({ spacing }) => ({
  textAlign: "center",
  marginHorizontal: spacing.md,
})

const $logoutLink: ThemedStyle<ViewStyle> = () => ({
  position: "absolute",
  bottom: 0,
  right: 0,
  padding: 8,
})

const $logoutText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.textDim,
  fontSize: 14,
  opacity: 0.7,
})
